import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { getApiUrl } from '../../configs/config';
import { useSubscriptionModalStore } from '../../utils/subscriptionModalStore';
import { useUserStore } from '../../utils/userStore';
import { RouterConstant } from '../../routers/index';


class HttpClient {
  private instance: AxiosInstance;

  constructor() {
    const baseURL = getApiUrl();
    this.instance = axios.create({
      baseURL,
      timeout: 120000, // 2 min
      withCredentials: true,
    });

    this.instance.interceptors.request.use(
      (config) => {
        // Don't set Content-Type for FormData - let browser handle it
        if (!(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json';
        }
        config.withCredentials = true;
        return config;
      },
      this.handleRequestError
    );

    this.instance.interceptors.request.use(
      this.handleRequestConfig,
      this.handleRequestError
    );

    this.instance.interceptors.response.use(
      this.handleSuccess,
      (error: any) => {
        if (axios.isAxiosError(error) && error.response?.status === 402) {
          useSubscriptionModalStore.getState().openModal();
        }
        return Promise.reject(error);
      }
    );
  }

  private handleRequestConfig<T>(config: InternalAxiosRequestConfig<any>): InternalAxiosRequestConfig<any> {
    config.withCredentials = true; // Ensure cookies are sent with the request
    console.log("Request Config:", config); // Log the request configuration
    return config;
  }

  private handleRequestError(error: any): Promise<any> {
    console.error("Request error:", error);
    return Promise.reject(error);
  }

  private handleSuccess<T>(response: AxiosResponse<T>): T {
    localStorage.setItem("shoonit_cookies_working", "true");
    return response.data;
  }

  private handleError(error: any): never {
    console.log("Request error:", error);
    if (axios.isAxiosError(error)) {
      const e = error as AxiosError;
      console.error("Axios Error Details:", {
        message: e.message,
        name: e.name,
        code: e.code,
        config: e.config,
        request: e.request,
        response: e.response,
      });

      const isLoggedIn = useUserStore.getState().isLoggedIn;
      const cookiesWorking = localStorage.getItem("shoonit_cookies_working") === "true";

      // Handle 401 Unauthorized or Network Errors
      if (e.response?.status === 401) {
        console.warn("Unauthorized: Redirecting to registration/login...");
        
        if (window.location.pathname.startsWith(RouterConstant.ROUTERS_PATH.CAPTUREEXERCISE)) {
          throw error;
        }

        useUserStore.getState().resetUser();
        localStorage.removeItem("shoonit_cookies_working");
        
        const detail = (e.response?.data as any)?.detail;
        const isTokenExpiredOrInvalid = typeof detail === "string" && (
          detail.includes("expired") || 
          detail.includes("Invalid") ||
          detail.includes("token") ||
          detail.includes("User not found")
        );

        if (isLoggedIn && !isTokenExpiredOrInvalid && !cookiesWorking) {
          window.location.href = `${RouterConstant.ROUTERS_PATH.USERREGISTRATIONINITIAL}?auth_err=cookie_blocked`;
        } else if (isLoggedIn) {
          window.location.href = `${RouterConstant.ROUTERS_PATH.LOGIN}?session_expired=true`;
        } else {
          window.location.href = RouterConstant.ROUTERS_PATH.USERREGISTRATIONINITIAL;
        }
      } else if (e.code === "ERR_NETWORK") {
        console.warn("Network Error");
        if (isLoggedIn && navigator.onLine && !cookiesWorking) {
          console.warn("CORS issue detected. Redirecting to registration with cookie_blocked...");
          useUserStore.getState().resetUser();
          localStorage.removeItem("shoonit_cookies_working");
          window.location.href = `${RouterConstant.ROUTERS_PATH.USERREGISTRATIONINITIAL}?auth_err=cookie_blocked`;
        }
      }
    }
    throw error;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig<any>): Promise<any> {
    try {
      const response = await this.instance.get<T>(url, config);
      return response;
    } catch (error: any) {
      this.handleError(error);
    }
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig<any>): Promise<any> {
    try {
      const response = await this.instance.post<T>(url, data, config);
      return response;
    } catch (error: any) {
      this.handleError(error);
    }
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig<any>): Promise<any> {
    try {
      const response = await this.instance.put<T>(url, data, config);
      return response;
    } catch (error: any) {
      this.handleError(error);
    }
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig<any>): Promise<any> {
    try {
      const response = await this.instance.patch<T>(url, data, config);
      return response;
    } catch (error: any) {
      this.handleError(error);
    }
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig<any>): Promise<any> {
    try {
      const response = await this.instance.delete<T>(url, config);
      return response;
    } catch (error: any) {
      this.handleError(error);
    }
  }
}

export { HttpClient };